export interface SubFeature {
  id: string;
  label: string;
  icon: string;
  route: string;
  description: string;
}

export interface NavigationModule {
  id: string;
  label: string;
  icon: string;
  route: string;
  description?: string;
  subFeatures?: SubFeature[];
  visible?: boolean; // For future role-based access
}

export const navigationModules: NavigationModule[] = [
  // {
  //   id: "dashboard",
  //   label: "Dashboard",
  //   icon: "ri-dashboard-2-line",
  //   route: "/dashboard",
  //   description: "Main dashboard overview",
  // },
  {
    id: "admin-dashboard",
    label: "Operations Command Center",
    icon: "ri-command-line",
    route: "/admin-dashboard",
    description: "Scaffold operations command center",
  },
  // {
  //   id: "executive-dashboard",
  //   label: "Executive Dashboard",
  //   icon: "ri-bar-chart-grouped-line",
  //   route: "/dashboard-modern",
  //   description: "Executive analytics dashboard",
  // },
  {
    id: "lead-management",
    label: "Lead",
    icon: "ri-user-search-line",
    route: "/leads",
    description: "Manage and track leads",
    subFeatures: [
      {
        id: "leads-list",
        label: "Lead Directory",
        icon: "ri-user-search-line",
        route: "/leads/list",
        description: "View and manage all leads",
      },
      {
        id: "leads-create",
        label: "Create Lead",
        icon: "ri-add-circle-line",
        route: "/leads/create",
        description: "Add a new lead",
      },
    ],
  },
  {
    id: "client-management",
    label: "Client",
    icon: "ri-building-2-line",
    route: "/clients",
    description: "Manage clients and relationships",
    subFeatures: [
      {
        id: "clients-list",
        label: "Client Directory",
        icon: "ri-building-2-line",
        route: "/clients/list",
        description: "View and manage all clients",
      },
      {
        id: "client-sites",
        label: "Client Sites",
        icon: "ri-map-pin-line",
        route: "/clients/sites",
        description: "Manage client locations",
      },
      {
        id: "client-contacts",
        label: "Client Contacts",
        icon: "ri-contacts-line",
        route: "/clients/contacts",
        description: "Manage client contacts",
      },
      {
        id: "meetings",
        label: "Client Meetings",
        icon: "ri-calendar-line",
        route: "/clients/meetings",
        description: "Schedule and manage meetings",
      },
    ],
  },
  {
    id: "quote-management",
    label: "Quote",
    icon: "ri-file-list-3-line",
    route: "/quotes",
    description: "Create and manage quotes",
    subFeatures: [
      {
        id: "quotes-list",
        label: "Quote List",
        icon: "ri-file-list-3-line",
        route: "/quotes/list",
        description: "View all quotes",
      },
      {
        id: "quotes-create",
        label: "Create Quote",
        icon: "ri-add-circle-line",
        route: "/quotes/create",
        description: "Create a new quote",
      },
    ],
  },
  {
    id: "job-management",
    label: "Job",
    icon: "ri-briefcase-line",
    route: "/job",
    description: "Manage jobs, work orders and scaffolds",
    subFeatures: [
      {
        id: "job-jobs",
        label: "Jobs",
        icon: "ri-briefcase-line",
        route: "/clients/jobs",
        description: "View and manage all jobs",
      },
      {
        id: "job-work-orders",
        label: "Work Orders",
        icon: "ri-file-list-line",
        route: "/clients/work-orders",
        description: "Manage work orders",
      },
      {
        id: "job-scaffolds",
        label: "Scaffolds",
        icon: "ri-tools-line",
        route: "/scaffolds/list",
        description: "View and manage scaffolds",
      },
    ],
  },
  {
    id: "scheduler",
    label: "Scheduler",
    icon: "ri-calendar-event-line",
    route: "/scheduler",
    description: "Schedule and manage scaffold tasks",
  },
  {
    // Kept as id "planner" so it stays in TopDockNav's dockModuleIds allowlist +
    // honours the enableDailyPlanner tenant toggle. The label is "Planner" but it
    // opens the Dispatch Board directly (the old Planner sub-pages are retired).
    id: "planner",
    label: "Planner",
    icon: "ri-calendar-todo-line",
    route: "/planner",
    description: "Daily dispatch board",
  },
  {
    id: "compliance",
    label: "Compliance",
    icon: "ri-shield-check-line",
    route: "/compliance",
    description: "Geo-zones, inspection groups and the compliance jobs report",
    subFeatures: [
      {
        id: "compliance-zones",
        label: "Compliance Zones",
        icon: "ri-map-pin-range-line",
        route: "/compliance/zones",
        description: "Draw geographic compliance regions on a map",
      },
      {
        id: "compliance-groups",
        label: "Compliance Groups",
        icon: "ri-team-line",
        route: "/compliance/groups",
        description: "Predefined inspection groups + members",
      },
      {
        id: "compliance-report",
        label: "Scaffolds Report",
        icon: "ri-stack-line",
        route: "/compliance/report",
        description: "Scaffolds mapped to zones, by site/client/status",
      },
      {
        id: "compliance-dispatch",
        label: "Dispatch",
        icon: "ri-route-line",
        route: "/compliance/dispatch",
        description: "What's due now — plan and route this week's checks",
      },
      {
        id: "compliance-inspections",
        label: "Scheduled Checks",
        icon: "ri-clipboard-line",
        route: "/compliance/inspections",
        description: "Schedule weekly compliance checks by zone",
      },
      {
        id: "compliance-fix-tasks",
        label: "FIX Tasks",
        icon: "ri-tools-line",
        route: "/compliance/fix-tasks",
        description: "Remediation tasks raised by failed compliance checks",
      },
    ],
  },
  {
    id: "scaffold",
    label: "Scaffold",
    icon: "ri-tools-line",
    route: "/scaffold",
    description: "Manage scaffold operations",
    subFeatures: [
      {
        id: "work-orders",
        label: "Work Orders",
        icon: "ri-file-list-line",
        route: "/clients/work-orders",
        description: "Manage work orders",
      },
      {
        id: "scaffolds-list",
        label: "Scaffolds",
        icon: "ri-tools-line",
        route: "/scaffolds/list",
        description: "View and manage scaffolds",
      },
    ],
  },
  {
    id: "timesheets",
    label: "Timesheets",
    icon: "ri-timer-line",
    route: "/timesheets",
    description: "Timesheet management and time tracking",
    subFeatures: [
      {
        id: "timesheet-list",
        label: "All Timesheets",
        icon: "ri-list-check",
        route: "/timesheets",
        description: "View and manage all timesheets",
      },
      {
        id: "daily-review",
        label: "Daily Review",
        icon: "ri-calendar-check-line",
        route: "/timesheets/daily-review",
        description: "Single-day operations review dashboard",
      },
      {
        id: "timesheet-create",
        label: "Create On Behalf",
        icon: "ri-user-add-line",
        route: "/timesheets/create",
        description: "Create timesheet for a worker",
      },
      {
        id: "field-timesheet",
        label: "Field Time Entry",
        icon: "ri-smartphone-line",
        route: "/field/time-tracking",
        description: "Worker mobile time tracking",
      },
      {
        id: "rate-matrix",
        label: "Rate Matrix",
        icon: "ri-money-dollar-circle-line",
        route: "/timesheets/rates",
        description: "Labour and cartage rate reference",
      },
      {
        id: "prep-charges",
        label: "Prep Charge Rules",
        icon: "ri-time-line",
        route: "/timesheets/prep-charges",
        description: "Loading/prep charge matrix",
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: "ri-money-dollar-circle-line",
    route: "/finance",
    description: "Manage financial operations",
  },
  {
    id: "inventory",
    label: "Inventory",
    icon: "ri-store-2-line",
    route: "/inventory",
    description: "Manage products and inventory",
    subFeatures: [
      {
        id: "inventory-dashboard",
        label: "Inventory Dashboard",
        icon: "ri-bar-chart-box-line",
        route: "/inventory/dashboard",
        description: "View inventory overview and analytics",
      },
      {
        id: "products",
        label: "Products",
        icon: "ri-shopping-bag-3-line",
        route: "/products/list",
        description: "Manage product catalog",
      },
      {
        id: "stock-items",
        label: "Stock Items",
        icon: "ri-box-3-line",
        route: "/stock-items/list",
        description: "Manage stock inventory",
      },
      {
        id: "purchase-orders",
        label: "Purchase Orders",
        icon: "ri-shopping-cart-2-line",
        route: "/purchase-orders/list",
        description: "Manage purchase orders",
      },
      {
        id: "suppliers",
        label: "Suppliers",
        icon: "ri-truck-line",
        route: "/suppliers/list",
        description: "Manage suppliers",
      },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: "ri-settings-4-line",
    route: "/settings",
    description: "System configuration and settings",
    subFeatures: [
      // HRMS hidden from Settings for UAT (2026-07-06) — out of scope for the
      // client. Route still works if navigated directly; re-enable by
      // uncommenting. Also hidden from the Settings module-home tiles.
      // {
      //   id: "hrms",
      //   label: "HRMS",
      //   icon: "ri-user-settings-line",
      //   route: "/settings/hrms",
      //   description: "Human resources — employees, leave, training, attendance & more",
      // },
      {
        id: "tenant-rental-config",
        label: "Global Rental Configuration",
        icon: "ri-settings-3-line",
        route: "/settings/tenant-rental-config",
        description: "Configure rental settings",
      },
      {
        id: "departments",
        label: "Departments",
        icon: "ri-building-line",
        route: "/account/departments",
        description: "Manage departments",
      },
      {
        id: "tenant-roles",
        label: "User Roles",
        icon: "ri-shield-user-line",
        route: "/account/tenant-roles",
        description: "Manage user roles and permissions",
      },
      {
        id: "global-users",
        label: "User Management",
        icon: "ri-team-line",
        route: "/account/global-users",
        description: "Manage system users",
      },
      {
        id: "tenant-locations",
        label: "Global Locations",
        icon: "ri-map-2-line",
        route: "/tenant-locations/list",
        description: "Manage global locations",
      },
      {
        id: "asset-management",
        label: "Asset Management",
        icon: "ri-truck-line",
        route: "/assets/list",
        description: "Manage assets (vehicles, tools, equipment)",
      },
      {
        id: "tenant-labour-rates",
        label: "Labour Rates",
        icon: "ri-hammer-line",
        route: "/tenant-labour-rates/list",
        description: "Configure labour rates",
      },
      {
        id: "site-types",
        label: "Site Types",
        icon: "ri-building-line",
        route: "/settings/site-types",
        description: "Manage site types",
      },
    ],
  },
];

/**
 * ── PRIMARY NAV VISIBILITY ─────────────────────────────────────────────────
 * SINGLE source of truth shared by the desktop top dock (TopDockNav) and the
 * mobile slide-out (MobileMenuOffcanvas) so both navs show the SAME modules.
 *  - To surface a top-level module, add its id to DOCK_NAV_MODULE_IDS.
 *  - To HIDE a module from BOTH navs, add its id to HIDDEN_NAV_MODULE_IDS
 *    (routes still work if navigated to directly).
 * Order always follows the navigationModules array above.
 */
export const DOCK_NAV_MODULE_IDS: readonly string[] = [
  "admin-dashboard",
  "lead-management",
  "client-management",
  "quote-management",
  "job-management",
  // "scheduler", // hidden from primary nav
  "planner",
  "timesheets",
  "compliance",
  "finance",
  "inventory",
  "settings",
];

export const HIDDEN_NAV_MODULE_IDS: ReadonlySet<string> = new Set<string>([
  "timesheets",
  "finance",
]);

/**
 * Base set of primary-nav modules — allowlisted and not hidden, in
 * navigationModules order. TopDockNav layers per-user permission and per-tenant
 * filters on top of this; the mobile off-canvas uses it as-is.
 */
export const getDockNavModules = (): NavigationModule[] =>
  navigationModules.filter(
    (module) =>
      DOCK_NAV_MODULE_IDS.includes(module.id) &&
      !HIDDEN_NAV_MODULE_IDS.has(module.id),
  );

/**
 * Field-app links for the WEB app's mobile menu. The admin web navigation isn't
 * built for phones, so on small screens (the Header hamburger / MobileMenuOffcanvas)
 * we surface ONLY the field-app (/field/*) entry points — never the desktop modules.
 * Mirrors the live routes in features/field-app (the other field items are still
 * "coming soon" there, so they're intentionally omitted here).
 */
export const FIELD_NAV_MODULES: NavigationModule[] = [
  {
    id: "field-home",
    label: "Field Home",
    icon: "ri-home-5-line",
    route: "/field",
    description: "Field app dashboard",
  },
  {
    id: "field-timesheet",
    label: "Timesheet",
    icon: "ri-time-line",
    route: "/field/time-tracking",
    description: "Worker time tracking",
  },
];

// Get primary modules for mobile bottom nav (max 4-5 items)
export const getPrimaryModules = (): NavigationModule[] => {
  return navigationModules.filter(
    (module) =>
      // module.id === "dashboard" ||
      module.id === "client-management" ||
      module.id === "quote-management" ||
      module.id === "lead-management"
  );
};

// Get secondary modules (for "More" menu)
export const getSecondaryModules = (): NavigationModule[] => {
  const primaryIds = getPrimaryModules().map((m) => m.id);
  return navigationModules.filter((module) => !primaryIds.includes(module.id));
};

// Get module by route
export const getModuleByRoute = (route: string): NavigationModule | undefined => {
  return navigationModules.find((module) => route.startsWith(module.route));
};

// Get sub-features for a module
export const getSubFeatures = (moduleId: string): SubFeature[] => {
  const module = navigationModules.find((m) => m.id === moduleId);
  return module?.subFeatures || [];
};

